import { Ng2StateDeclaration, UIRouterModule } from '@uirouter/angular';
import { Component } from '@angular/core';
import { SessionService } from '@monsido/core/session/session.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { StateService, Transition } from '@uirouter/core';
import { HasAccessService } from '@client/app/pipe/has-access/has-access.service';
import { CustomerHasPlanPipe } from '@client/ng2/shared/pipes/customer-has-plan/customer-has-plan.pipe';
import { MonUIRouterTransitionService } from '@monsido/services/ui-router-transition-service/ui-router-transition-service';
import { MON_MODULES } from '@monsido/ng2/core/constants/mon-modules.constant';

// Components
import { PrivacySummaryComponent } from './summary/privacy-summary.component';
import { PrivacyIssuesComponent } from './issues/privacy-issues.component';
import { PrivacyChecklistComponent } from './checklist/privacy-checklist.component';
import { DomainDataPrivacySettingsComponent } from '../components/domain-settings/domain-data-privacy-settings.component';
import { PrivacyListComponent } from './list/list.component';
import { CookieAcceptanceRateOverviewComponent } from '../../cookie-banner/pages/cookie-acceptance-rate-overview/cookie-acceptance-rate-overview.component';
import { ConsentLogComponent } from '../../cookie-banner/pages/consent-log/consent-log.component';
import { CookieOverviewComponent } from '../../cookie-banner/pages/cookie-overview/cookie-overview.component';

@Component({
    selector: 'base-data-privacy',
    template: `<ui-view name="content"></ui-view>`,
    standalone: true,
    imports: [UIRouterModule],
})
class BaseDataPrivacyComponent {}

@Component({
    selector: 'base-data-privacy-consent-overview',
    template: `<ui-view></ui-view>`,
    standalone: true,
    imports: [UIRouterModule],
})
class BaseDataPrivacyConsentOverviewComponent {}

// Helper functions
function checkUserHasAccessToDataPrivacy (hasAccessService: HasAccessService, sessionService: SessionService) {
    return hasAccessService.hasAccess(sessionService.me || undefined, MON_MODULES.data_privacy);
}

function checkDomainHasAccessToDataPrivacy (activeFeatureService: ActiveFeatureService, sessionService: SessionService) {
    if (!sessionService.domain) {
        return false;
    }
    return activeFeatureService.isFeatureActive(MON_MODULES.data_privacy, sessionService.domain);
}

function checkDataPrivacyFeatureEnabled (activeFeatureService: ActiveFeatureService) {
    return activeFeatureService.isFeatureActive(MON_MODULES.data_privacy);
}

function checkHasAccessToConsentManager (sessionService: SessionService, customerHasPlanPipe: CustomerHasPlanPipe, activeFeatureService: ActiveFeatureService) {
    const hasConsentManagerPlanTrait = sessionService.customer && sessionService.customer.plan_traits?.cookie_banner;

    return (
        hasConsentManagerPlanTrait &&
        (customerHasPlanPipe.transform('demo') || activeFeatureService.isFeatureActive(MON_MODULES.cookie_banner))
    );
}

// Resolver functions
const redirectInCaseOfLimitedAccessToDataPrivacy = [
    StateService,
    Transition,
    SessionService,
    ActiveFeatureService,
    MonUIRouterTransitionService,
    HasAccessService,
    CustomerHasPlanPipe,
    (
        stateService: StateService,
        transition: Transition,
        sessionService: SessionService,
        activeFeatureService: ActiveFeatureService,
        monUIRouterTransitionService: MonUIRouterTransitionService,
        hasAccessService: HasAccessService,
        customerHasPlanPipe: CustomerHasPlanPipe,
    ) => {
        const hasAccessToDataPrivacy = checkUserHasAccessToDataPrivacy(hasAccessService, sessionService) && checkDomainHasAccessToDataPrivacy(activeFeatureService, sessionService);
        const dataPrivacyFeatureIsEnabled = checkDataPrivacyFeatureEnabled(activeFeatureService);
        const accessToConsentManager = checkHasAccessToConsentManager(sessionService, customerHasPlanPipe, activeFeatureService);

        if ((!hasAccessToDataPrivacy || !dataPrivacyFeatureIsEnabled) && accessToConsentManager) {
            // TODO: https://optimere.atlassian.net/browse/MS-1011
            // removed the MonUIRouterTransitionService wrapper of this $state transition because it does not work in this case.
            stateService.go('base.customer.domain.data-privacy.consent-overview.cookie-acceptance-rate', transition.params());
            return;
        }

        if (!hasAccessToDataPrivacy) {
            transition.abort();
            return stateService.go('base.customer.domain.dashboard', transition.params());
        }

        return Promise.resolve(true);
    },
];

const userHasAccessToDataPrivacy = [
    StateService,
    Transition,
    HasAccessService,
    SessionService,
    MonUIRouterTransitionService,
    (
        stateService: StateService,
        transition: Transition,
        hasAccessService: HasAccessService,
        sessionService: SessionService,
        monUIRouterTransitionService: MonUIRouterTransitionService,
    ) => {
        if (checkUserHasAccessToDataPrivacy(hasAccessService, sessionService)) {
            return Promise.resolve(true);
        }
        return monUIRouterTransitionService.onTransitionFinished(() => {
            stateService.go('base.customer.domain.dashboard', transition.params());
        });
    },
];

const domainHasAccessToDataPrivacy = [
    ActiveFeatureService,
    SessionService,
    StateService,
    Transition,
    (
        activeFeatureService: ActiveFeatureService,
        sessionService: SessionService,
        stateService: StateService,
        transition: Transition,
    ) => {
        const hasAccess = checkDomainHasAccessToDataPrivacy(activeFeatureService, sessionService);
        if (hasAccess) {
            return Promise.resolve(true);
        }

        transition.abort();
        stateService.go('base.customer.domain.dashboard', transition.params());
        return Promise.reject(new Error('Domain validation failed'));
    },
];

const dataPrivacyFeatureEnabled = [
    ActiveFeatureService,
    StateService,
    Transition,
    SessionService,
    MonUIRouterTransitionService,
    (
        activeFeatureService: ActiveFeatureService,
        stateService: StateService,
        transition: Transition,
        sessionService: SessionService,
        monUIRouterTransitionService: MonUIRouterTransitionService,
    ) => {
        if (checkDataPrivacyFeatureEnabled(activeFeatureService)) {
            return Promise.resolve(true);
        }
        return monUIRouterTransitionService.onTransitionFinished(() => {
            stateService.go('base.customer.domain.dashboard', transition.params());
        });
    },
];

const hasAccessToConsentManager = [
    StateService,
    Transition,
    SessionService,
    CustomerHasPlanPipe,
    ActiveFeatureService,
    MonUIRouterTransitionService,
    (
        stateService: StateService,
        transition: Transition,
        sessionService: SessionService,
        customerHasPlanPipe: CustomerHasPlanPipe,
        activeFeatureService: ActiveFeatureService,
        monUIRouterTransitionService: MonUIRouterTransitionService,
    ) => {
        if (checkHasAccessToConsentManager(sessionService, customerHasPlanPipe, activeFeatureService)) {
            return Promise.resolve(true);
        }

        return monUIRouterTransitionService.onTransitionFinished(() => {
            stateService.go('base.customer.domain.data-privacy.summary', transition.params());
        });
    },
];

const isCustomerAdmin = [
    StateService,
    Transition,
    SessionService,
    MonUIRouterTransitionService,
    (
        stateService: StateService,
        transition: Transition,
        sessionService: SessionService,
        monUIRouterTransitionService: MonUIRouterTransitionService,
    ) => {
        const isAdmin = sessionService.hasRole('customer_admin');
        if (isAdmin) {
            return Promise.resolve(true);
        }
        return monUIRouterTransitionService.onTransitionFinished(() => {
            stateService.go('base.customer.domain.data-privacy.consent-overview', transition.params());
        });
    },
];

export const DATA_PRIVACY_STATES: Ng2StateDeclaration[] = [
    {
        name: 'base.customer.domain.data-privacy',
        url: '/data-privacy',
        abstract: true,
        views: {
            'content@base.customer.domain': {
                component: BaseDataPrivacyComponent,
            },
        },
        data: {
            sidebar: 'data-privacy',
            title: 'Acquia Web Governance - Data Privacy',
            // No shellPath here: abstract parents are not navigation targets.
            // The prefix is owned by data_privacy.summary below (url '' — same path).
        },
    },
    {
        name: 'base.customer.domain.data-privacy.consent-overview',
        url: '/consent-overview',
        views: {
            'content@base.customer.domain.data-privacy': {
                component: BaseDataPrivacyConsentOverviewComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Cookie Acceptance Rate',
            sidebar: 'data-privacy',
            shellPath: '/domains/:domainId/data-privacy/consent-overview',
        },
    },
    {
        name: 'base.customer.domain.data-privacy.summary',
        url: '',
        views: {
            'content@base.customer.domain.data-privacy': {
                component: PrivacySummaryComponent,
            },
        },
        /** Shell-facing path for CC breadcrumb/menu sync (see event-bus-bridge.init.ts) */
        data: { title: 'Acquia Web Governance - Data Privacy Summary', shellPath: '/domains/:domainId/data-privacy' },
        resolve: {
            redirectInCaseOfLimitedAccessToDataPrivacy: redirectInCaseOfLimitedAccessToDataPrivacy,
            domainHasAccessToDataPrivacy,
        },
    },
    {
        name: 'base.customer.domain.data-privacy.issues',
        url: '/issues?page&page_size&tab&search',
        params: {
            page: { dynamic: true },
            page_size: { dynamic: true },
            tab: { dynamic: true },
            search: { dynamic: true },
        },
        views: {
            'content@base.customer.domain.data-privacy': {
                component: PrivacyIssuesComponent,
            },
        },
        data: { title: 'Acquia Web Governance - Data Privacy Issues', shellPath: '/domains/:domainId/data-privacy/issues' },
        resolve: {
            dataPrivacyFeatureEnabled: dataPrivacyFeatureEnabled,
            userHasAccessToDataPrivacy: userHasAccessToDataPrivacy,
            domainHasAccessToDataPrivacy,
        },
    },
    {
        name: 'base.customer.domain.data-privacy.checklist',
        url: '/checklist?page&page_size&tab&search',
        params: {
            page: { dynamic: true },
            page_size: { dynamic: true },
            tab: { dynamic: true },
            search: { dynamic: true },
        },
        views: {
            'content@base.customer.domain.data-privacy': {
                component: PrivacyChecklistComponent,
            },
        },
        data: { title: 'Acquia Web Governance - Data Privacy Check List', shellPath: '/domains/:domainId/data-privacy/checklist' },
        resolve: {
            dataPrivacyFeatureEnabled: dataPrivacyFeatureEnabled,
            userHasAccessToDataPrivacy: userHasAccessToDataPrivacy,
            domainHasAccessToDataPrivacy,
        },
    },
    {
        name: 'base.customer.domain.data-privacy.domain-settings',
        url: '/domain-settings',
        views: {
            'content@base.customer.domain.data-privacy': {
                component: DomainDataPrivacySettingsComponent,
            },
        },
        data: { title: 'Acquia Web Governance - Data Privacy Guidelines', shellPath: '/domains/:domainId/data-privacy/domain-settings' },
        resolve: {
            dataPrivacyFeatureEnabled: dataPrivacyFeatureEnabled,
            userHasAccessToDataPrivacy: userHasAccessToDataPrivacy,
            domainHasAccessToDataPrivacy,
        },
    },
    {
        name: 'base.customer.domain.data-privacy.index',
        url: '/list?page&page_size&tab&search',
        params: {
            page: { dynamic: true },
            page_size: { dynamic: true },
            tab: { dynamic: true },
            search: { dynamic: true },
        },
        views: {
            'content@base.customer.domain.data-privacy': {
                component: PrivacyListComponent,
            },
        },
        data: { title: 'Acquia Web Governance - Data Privacy Content', shellPath: '/domains/:domainId/data-privacy/list' },
        resolve: {
            dataPrivacyFeatureEnabled: dataPrivacyFeatureEnabled,
            userHasAccessToDataPrivacy: userHasAccessToDataPrivacy,
            domainHasAccessToDataPrivacy,
        },
    },
    {
        name: 'base.customer.domain.data-privacy.consent-overview.cookie-acceptance-rate',
        url: '/cookie-acceptance-rate',
        views: {
            'content@base.customer.domain.data-privacy': {
                component: CookieAcceptanceRateOverviewComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Cookie Acceptance Rate',
            sidebar: 'data-privacy',
            shellPath: '/domains/:domainId/data-privacy/consent-overview/cookie-acceptance-rate',
        },
        resolve: {
            hasAccessToConsentManager: hasAccessToConsentManager,
        },
    },
    {
        name: 'base.customer.domain.data-privacy.consent-overview.consent-log',
        url: '/consent-log?from&to&uuid',
        params: {
            from: { dynamic: true },
            to: { dynamic: true },
            uuid: { dynamic: true },
        },
        views: {
            'content@base.customer.domain.data-privacy': {
                component: ConsentLogComponent,
            },
        },
        data: {
            title: 'Acquia Web Governance - Cookie Consent Log',
            sidebar: 'data-privacy',
            shellPath: '/domains/:domainId/data-privacy/consent-overview/consent-log',
        },
        resolve: {
            hasAccessToConsentManager: hasAccessToConsentManager,
        },
    },
    {
        name: 'base.customer.domain.data-privacy.consent-overview.cookie-overview',
        url: '/cookie-overview',
        views: {
            'content@base.customer.domain.data-privacy': {
                component: CookieOverviewComponent,
            },
        },
        data: { title: 'Acquia Web Governance - Cookie Overview', sidebar: 'data-privacy', shellPath: '/domains/:domainId/data-privacy/consent-overview/cookie-overview' },
        resolve: {
            hasAccessToConsentManager: hasAccessToConsentManager,
            isCustomerAdmin: isCustomerAdmin,
        },
    },
];
